import { createSelector } from 'reselect';
import {
  AGE,
  AGES,
  MARITAL_STATUS,
  PARTNER_NAME,
  CHILDREN,
  CHILDREN_DETAILS,
  TAX_FILING_STATE,
  INCOME,
  EXPENSES,
  EARMARKED_ACCOUNTS,
  CHILDREN_DETAILS_EDUCATION,
  ENROLLMENT_AGE,
  SPECIFIC_COLLEGE,
  COLLEGE_NAME,
  TUITION_TYPE,
  DURATION,
  SCHOOL_TYPE,
  EXPECTED_TUITION,
  DEBTS,
  HOME_PURCHASE_YEAR,
  HOME_PURCHASE_VALUE,
  MORTGAGE,
  DOWN_PAYMENT,
  MORTGAGE_DETAILS,
  PROPERTY_TAX,
  HOMEOWNER_INSURANCE,
  HOA,
  HOA_DETAILS,
  LARGE_PURCHASE_VALUE,
  LARGE_PURCHASE_NAME,
  LARGE_PURCHASE_YEAR,
  EMERGENCY_FUND_TARGET_YEAR,
  EMERGENCY_FUND_TARGET_AMOUNT,
  PENSIONS,
  HEALTHCARE_START_AT_RETIREMENT,
  HEALTHCARE_ANNUAL_EXPENSE,
  PROPERTY_NAME,
  DECUMULATION_YEARLY_DISTRIBUTION,
  DECUMULATION_DISTRIBUTION_YEARS,
} from './SummaryConstants';
import { iteratorComparator } from '../AdvWorkflow/AdvWorkflowHelpers';
import {
  data as dataSelector,
  data2 as data2Selector,
  isPopulatedSelector,
  stepsToSkipSelector,
} from '../AdvWorkflow/AdvWorkflowSelector';
import { BACKEND_KEY as PERSONAL_INFO_BACKEND_KEY } from '../PersonalInfoWorkflow/PersonalInfoWorkflowConstants';
import {
  yearlySalary as yearlySalarySelector,
  partnerYearlySalary as partnerYearlySalarySelector,
} from '../IncomeWorkflow/IncomeWorkflowSelector';
import { selectTotalExpensesAmount } from '../ExpensesWorkflow/ExpensesWorkflowSelector';

import { accountsOverviewSelector } from '../AccountsWorkflow/Steps/AccountsStep/AccountsOverviewSelector';
import nullableSelectedFilingStateSelector from '../TaxesWorkflow/TaxesWorkflowSelector';

import { WORKFLOW_ID as FINANCIAL_ACCOUNTS_WORKFLOW_ID } from '../FinancialAccountsWorkflow/FinancialAccountsWorkflowConstants';
import { WORKFLOW_ID as DEBT_MANAGEMENT_WORKFLOW_ID } from '../DebtManagementWorkflow/DebtManagementWorkflowConstants';

import { OTHER_TUITION } from '../ChildrenInfoWorkflow/ChildrenInfoWorkflowConstants';

import {
  childFirstNameOrDefaultSelector,
  enrollAgeOrDefaultSelector,
  isSpecificCollegeOrDefaultSelector,
  collegeNameOrDefaultSelector,
  tuitionTypeOrDefaultSelector,
  hasTuitionTypeOrDefaultSelector,
  durationOrDefaultSelector,
  educationTypeOrDefaultSelector,
  schoolTypeOrDefaultSelector,
  expectedTuitionOrDefaultSelector,
} from '../ChildrenInfoWorkflow/ChildrenInfoWorkflowSelector';

import pensionsSelector from '../PensionWorkflow/Steps/PensionsOverviewSelector';

import { accountsOverviewSelector as debtsOverviewSelector } from '../DebtManagementDebtsWorkflow/Steps/AccountsStep/AccountsOverviewSelector';

import { nullablePropertyNameSelector } from '../HomePurchaseWorkflow/Steps/PropertyName/PropertyNameSelector';
import { yearHomePurchaseOrDefaultSelector } from '../HomePurchaseWorkflow/Steps/YearHomePurchase/YearHomePurchaseSelector';
import { nullableMarketValueSelector } from '../HomePurchaseWorkflow/Steps/HomeEstimatedValue/HomeEstimatedValueSelector';
import { mortgageDetailsOrDefaultSelector } from '../MortgageWorkflow/Steps/Mortgage/MortgageSelector';
import { nullableHasPlanToGetMortgageSelector } from '../MortgageWorkflow/Steps/PlanToGetMortgage/PlanToGetMortgageSelector';

import nullableDownPaymentSelector from '../MortgageWorkflow/Steps/DownPayment/DownPaymentSelector';
import propertyTaxOrDefaultSelector from '../HomePurchaseWorkflow/Steps/AnnualPropertyTax/AnnualPropertyTaxSelector';
import nullableAnnualHomeownersInsuranceSelector from '../HomePurchaseWorkflow/Steps/ExpectedAnnualHomeownerInsurance/ExpectedAnnualHomeownerInsuranceSelector';
import nullableHomeownerAssociationFeeSelector from '../HomePurchaseWorkflow/Steps/HomeownersAssociationFee/HomeownersAssociationFeeSelector';
import { yesOrNoHomeownerAssociationFeeSelector } from '../HomePurchaseWorkflow/Steps/HasHomeownerAssociationFee/HasHomeownerAssociationFeeSelector';
import { largePurchaseNameOrDefaultSelector } from '../LargePurchaseWorkflow/Steps/ItemDescription/ItemDescriptionSelector';
import { largePurchaseAmountOrDefaultSelector } from '../LargePurchaseWorkflow/Steps/PurchaseAmount/PurchaseAmountSelector';
import { largePurchaseYearOrDefaultSelector } from '../LargePurchaseWorkflow/Steps/PurchaseYear/PurchaseYearSelector';

import {
  emergencyFundTargetAmountOrDefaultSelector,
  emergencyFundTargetYearOrDefaultSelector,
} from '../EmergencyFundWorkflow/EmergencyFundWorkflowSelector';

import { healthcareGoalSelector } from '../HealthcareWorkflow/HealthcareWorkflowSelector';

import dictionarySelector from './SummaryDictionarySelector';

import { isNumeric } from '../../../js/app/react/validators';
import {
  selectDistributionDollarAmount,
  selectDistributionPercentage,
  selectDistributionStrategyType,
  selectDistributionYears,
} from '../DecumulationWorkflow/DecumulationWorkflow.selector';

export const formatMoney = value =>
  `$${window.ADV.util.formatValue(window.ADV.util.numberFrom(value), 'money')}`;
export const formatPercentage = value =>
  `${window.ADV.util.formatValue(window.ADV.util.numberFrom(value), 'percentage')}`;

const plurify = (number, noun) => {
  const PLURAL_EXCEPTIONS = {
    child: 'children',
  };

  if (number === 0) {
    return '-';
  }
  if (number === 1) {
    return `${number} ${noun}`;
  }
  if (PLURAL_EXCEPTIONS[noun]) {
    return `${number} ${PLURAL_EXCEPTIONS[noun]}`;
  }
  return `${number} ${noun}s`;
};

export const buildHouseholdList = (
  dictionary,
  graph,
  frontEndState,
  nullableSelectedFilingState,
  yearlySalary,
  partnerYearlySalary,
  averageSpent,
) => {
  const list = [];
  const hasPartner = frontEndState(`${PERSONAL_INFO_BACKEND_KEY}.HasPartner`) || false;

  if (dictionary[AGES] && hasPartner) {
    list.push({
      ...dictionary[AGES],
      details: `${graph(`${PERSONAL_INFO_BACKEND_KEY}.Principal.Age`)}, ${graph(
        `${PERSONAL_INFO_BACKEND_KEY}.Partner.Age`,
      )}`,
    });
  }

  if (dictionary[AGE] && !hasPartner) {
    list.push({
      ...dictionary[AGE],
      details: `${graph(`${PERSONAL_INFO_BACKEND_KEY}.Principal.Age`)}`,
    });
  }

  if (dictionary[MARITAL_STATUS]) {
    list.push({
      ...dictionary[MARITAL_STATUS],
      details: frontEndState(`${PERSONAL_INFO_BACKEND_KEY}.HasPartner`) ? 'Yes' : 'No',
    });
  }
  if (dictionary[PARTNER_NAME]) {
    if (frontEndState(`${PERSONAL_INFO_BACKEND_KEY}.HasPartner`)) {
      list.push({
        ...dictionary[PARTNER_NAME],
        details: `${graph(`${PERSONAL_INFO_BACKEND_KEY}.Partner.FirstName`)}`,
      });
    }
  }

  if (dictionary[CHILDREN]) {
    list.push({
      ...dictionary[CHILDREN],
      details: frontEndState(`${PERSONAL_INFO_BACKEND_KEY}.HasChildren`) ? 'Yes' : 'No',
    });
  }

  if (dictionary[CHILDREN_DETAILS]) {
    if (frontEndState(`${PERSONAL_INFO_BACKEND_KEY}.HasChildren`)) {
      const numberOfChildren = graph(`${PERSONAL_INFO_BACKEND_KEY}.Dependents`).length;
      list.push({
        ...dictionary[CHILDREN_DETAILS],
        details: `${plurify(numberOfChildren, 'child')}`,
      });
    }
  }

  if (dictionary[TAX_FILING_STATE]) {
    if (nullableSelectedFilingState) {
      list.push({
        ...dictionary[TAX_FILING_STATE],
        details: nullableSelectedFilingState,
      });
    }
  }

  if (dictionary[INCOME]) {
    if (frontEndState(`${PERSONAL_INFO_BACKEND_KEY}.HasPartner`)) {
      list.push({
        ...dictionary[INCOME],
        details: `${formatMoney(yearlySalary)} / ${formatMoney(partnerYearlySalary)}`,
      });
    } else {
      list.push({
        ...dictionary[INCOME],
        details: `${formatMoney(yearlySalary)}`,
      });
    }
  }

  if (dictionary[EXPENSES]) {
    list.push({
      ...dictionary[EXPENSES],
      details: `${formatMoney(averageSpent)}`,
    });
  }
  return list;
};

export const buildItemDebtManagementList = (
  dictionary,
  graph,
  frontEndState,
  nullableSelectedFilingState,
  yearlySalary,
  partnerYearlySalary,
  averageSpent,
  debtsOrDefault,
) => {
  const list = buildHouseholdList(
    dictionary,
    graph,
    frontEndState,
    nullableSelectedFilingState,
    yearlySalary,
    partnerYearlySalary,
    averageSpent,
  );

  if (dictionary[DEBTS]) {
    list.push({
      ...dictionary[DEBTS],
      details: `${plurify(debtsOrDefault.length, 'debt')}`,
    });
  }
  return list;
};

export const buildHomePurchaseItemList = (
  dictionary,
  propertyName,
  purchaseYearOrDefault,
  homeValueOrDefault,
  mortgageOrDefault,
  downPaymentOrDefault,
  mortgageDetailsOrDefault,
  propertyTaxOrDefault,
  homeownersInsuranceOrDefault,
  HOAOrDefault,
  HOADetailsOrDefault,
) => {
  const list = [];

  if (dictionary[PROPERTY_NAME]) {
    list.push({
      ...dictionary[PROPERTY_NAME],
      details: propertyName,
    });
  }

  if (dictionary[HOME_PURCHASE_YEAR]) {
    list.push({
      ...dictionary[HOME_PURCHASE_YEAR],
      details: `${purchaseYearOrDefault}`,
    });
  }

  if (dictionary[HOME_PURCHASE_VALUE]) {
    let marketValue = homeValueOrDefault;
    if (homeValueOrDefault === null) {
      marketValue = 0;
    }
    list.push({
      ...dictionary[HOME_PURCHASE_VALUE],
      details: `${formatMoney(marketValue)}`,
    });
  }

  if (dictionary[MORTGAGE]) {
    list.push({
      ...dictionary[MORTGAGE],
      details: mortgageOrDefault ? 'Yes' : 'No',
    });

    if (mortgageOrDefault) {
      if (dictionary[DOWN_PAYMENT]) {
        list.push({
          ...dictionary[DOWN_PAYMENT],
          details: `${formatMoney(downPaymentOrDefault)}`,
        });
      }

      if (dictionary[MORTGAGE_DETAILS]) {
        list.push({
          ...dictionary[MORTGAGE_DETAILS],
          details: `${mortgageDetailsOrDefault}`,
        });
      }
    }
  }

  if (dictionary[PROPERTY_TAX]) {
    list.push({
      ...dictionary[PROPERTY_TAX],
      details: `${formatMoney(propertyTaxOrDefault)}`,
    });
  }

  if (dictionary[HOMEOWNER_INSURANCE]) {
    list.push({
      ...dictionary[HOMEOWNER_INSURANCE],
      details: `${formatMoney(homeownersInsuranceOrDefault)}`,
    });
  }

  if (dictionary[HOA]) {
    list.push({
      ...dictionary[HOA],
      details: HOAOrDefault ? 'Yes' : 'No',
    });

    if (HOAOrDefault) {
      if (dictionary[HOA_DETAILS]) {
        list.push({
          ...dictionary[HOA_DETAILS],
          details: `${formatMoney(HOADetailsOrDefault)}`,
        });
      }
    }
  }

  return list;
};

export const buildItemList = (
  dictionary,
  graph,
  frontEndState,
  nullableSelectedFilingState,
  yearlySalary,
  partnerYearlySalary,
  averageSpent,
  accounts,
  childFirstNameOrDefault,
  enrollAgeOrDefault,
  isSpecificCollegeOrDefault,
  collegeNameOrDefault,
  hasTuitionTypeOrDefault,
  tuitionTypeOrDefault,
  durationOrDefault,
  educationTypeOrDefault,
  schoolTypeOrDefault,
  expectedTuitionOrDefault,
  propertyName,
  purchaseYearOrDefault,
  homeValueOrDefault,
  mortgageOrDefault,
  downPaymentOrDefault,
  mortgageDetailsOrDefault,
  propertyTaxOrDefault,
  homeownersInsuranceOrDefault,
  HOAOrDefault,
  HOADetailsOrDefault,
  largePurchaseItemOrDefault,
  largePurchaseValueOrDefault,
  largePurchaseYearOrDefault,
  debtsOverview,
  emergencyFundTargetAmount,
  emergencyFundTargetYear,
  pensionsOrDefault,
  stepsToSkipMapOrDefault,
  healthcareGoal,
  decumulationDistributionStrategyType,
  decumulationDistributionPercentage,
  decumulationDistributionDollarAmount,
  decumulationDistributionYears,
) => {
  let list = buildHouseholdList(
    dictionary,
    graph,
    frontEndState,
    nullableSelectedFilingState,
    yearlySalary,
    partnerYearlySalary,
    averageSpent,
  );
  if (dictionary[CHILDREN_DETAILS_EDUCATION]) {
    if (childFirstNameOrDefault !== '') {
      list.push({
        ...dictionary[CHILDREN_DETAILS_EDUCATION],
        details: `${childFirstNameOrDefault}`,
      });
    } else {
      list.push({
        ...dictionary[CHILDREN_DETAILS_EDUCATION],
        details: '-',
      });
    }
  }

  if (childFirstNameOrDefault !== '') {
    if (dictionary[ENROLLMENT_AGE]) {
      list.push({
        ...dictionary[ENROLLMENT_AGE],
        details: `${enrollAgeOrDefault}`,
      });
    }

    if (dictionary[SPECIFIC_COLLEGE]) {
      if (isSpecificCollegeOrDefault !== null) {
        list.push({
          ...dictionary[SPECIFIC_COLLEGE],
          details: isSpecificCollegeOrDefault ? 'Yes' : 'No',
        });
      }

      if (isSpecificCollegeOrDefault) {
        if (dictionary[COLLEGE_NAME]) {
          list.push({
            ...dictionary[COLLEGE_NAME],
            details: collegeNameOrDefault,
          });
        }

        if (hasTuitionTypeOrDefault) {
          if (dictionary[TUITION_TYPE]) {
            list.push({
              ...dictionary[TUITION_TYPE],
              details: tuitionTypeOrDefault,
            });
          }
        }
      }
    }

    if (
      (isSpecificCollegeOrDefault === true || educationTypeOrDefault === OTHER_TUITION) &&
      dictionary[DURATION]
    ) {
      if (isNumeric(durationOrDefault)) {
        list.push({
          ...dictionary[DURATION],
          details: `${plurify(+durationOrDefault, 'Year')}`,
        });
      }
    }

    if (dictionary[SPECIFIC_COLLEGE]) {
      if (!isSpecificCollegeOrDefault) {
        if (dictionary[SCHOOL_TYPE]) {
          list.push({
            ...dictionary[SCHOOL_TYPE],
            details: `${schoolTypeOrDefault}`,
          });
        }

        if (dictionary[EXPECTED_TUITION] && educationTypeOrDefault === OTHER_TUITION) {
          list.push({
            ...dictionary[EXPECTED_TUITION],
            details: `${formatMoney(expectedTuitionOrDefault)}`,
          });
        }
      }
    }
  }

  if (dictionary[HEALTHCARE_START_AT_RETIREMENT]) {
    list.push({
      ...dictionary[HEALTHCARE_START_AT_RETIREMENT],
      details: healthcareGoal.StartAtRetirement ? 'At Retirement' : healthcareGoal.StartYear,
    });
  }

  if (dictionary[HEALTHCARE_ANNUAL_EXPENSE]) {
    list.push({
      ...dictionary[HEALTHCARE_ANNUAL_EXPENSE],
      details: healthcareGoal.EstimateExpenses
        ? 'Automatically estimate'
        : `${formatMoney(healthcareGoal.AnnualExpense)}`,
    });
  }

  if (dictionary[EARMARKED_ACCOUNTS]) {
    const numberOfAccounts = accounts.filter(({ isSelected }) => isSelected).length;
    list.push({
      ...dictionary[EARMARKED_ACCOUNTS],
      details: `${plurify(numberOfAccounts, 'account')}`,
    });
  }

  if (dictionary[PENSIONS] && !stepsToSkipMapOrDefault.Pensions) {
    list.push({
      ...dictionary[PENSIONS],
      details: `${plurify(pensionsOrDefault.length, 'pension')}`,
    });
  }

  if (dictionary[LARGE_PURCHASE_NAME]) {
    if (largePurchaseItemOrDefault) {
      list.push({
        ...dictionary[LARGE_PURCHASE_NAME],
        details: `${largePurchaseItemOrDefault}`,
      });
      if (dictionary[LARGE_PURCHASE_VALUE]) {
        list.push({
          ...dictionary[LARGE_PURCHASE_VALUE],
          details: `${formatMoney(largePurchaseValueOrDefault)}`,
        });
      }

      if (dictionary[LARGE_PURCHASE_YEAR]) {
        list.push({
          ...dictionary[LARGE_PURCHASE_YEAR],
          details: `${largePurchaseYearOrDefault}`,
        });
      }
    }
  }

  if (dictionary[DEBTS]) {
    list.push({
      ...dictionary[DEBTS],
      details: `${plurify(debtsOverview.length, 'debt')}`,
    });
  }

  if (dictionary[EMERGENCY_FUND_TARGET_AMOUNT] && emergencyFundTargetAmount !== null) {
    list.push({
      ...dictionary[EMERGENCY_FUND_TARGET_AMOUNT],
      details: `${formatMoney(emergencyFundTargetAmount)}`,
    });
  }

  if (dictionary[EMERGENCY_FUND_TARGET_YEAR] && emergencyFundTargetAmount !== null) {
    list.push({
      ...dictionary[EMERGENCY_FUND_TARGET_YEAR],
      details: `${emergencyFundTargetYear}`,
    });
  }

  if (dictionary[DECUMULATION_YEARLY_DISTRIBUTION]) {
    if (decumulationDistributionStrategyType === 0 && decumulationDistributionPercentage !== null) {
      list.push({
        ...dictionary[DECUMULATION_YEARLY_DISTRIBUTION],
        details: formatPercentage(decumulationDistributionPercentage),
      });
    }

    if (
      decumulationDistributionStrategyType === 1 &&
      decumulationDistributionDollarAmount !== null
    ) {
      list.push({
        ...dictionary[DECUMULATION_YEARLY_DISTRIBUTION],
        details: `${formatMoney(decumulationDistributionDollarAmount)}`,
      });
    }
  }

  if (dictionary[DECUMULATION_DISTRIBUTION_YEARS] && decumulationDistributionYears !== null) {
    list.push({
      ...dictionary[DECUMULATION_DISTRIBUTION_YEARS],
      details: `${decumulationDistributionYears} year${
        decumulationDistributionYears > 1 ? 's' : ''
      }`,
    });
  }

  list = list.concat(
    buildHomePurchaseItemList(
      dictionary,
      propertyName,
      purchaseYearOrDefault,
      homeValueOrDefault,
      mortgageOrDefault,
      downPaymentOrDefault,
      mortgageDetailsOrDefault,
      propertyTaxOrDefault,
      homeownersInsuranceOrDefault,
      HOAOrDefault,
      HOADetailsOrDefault,
    ),
  );

  return list;
};

export const itemListNonDebtManagementSelector = createSelector(
  dictionarySelector,
  dataSelector,
  data2Selector,
  nullableSelectedFilingStateSelector,
  yearlySalarySelector,
  partnerYearlySalarySelector,
  selectTotalExpensesAmount,
  accountsOverviewSelector,
  childFirstNameOrDefaultSelector,
  enrollAgeOrDefaultSelector,
  isSpecificCollegeOrDefaultSelector,
  collegeNameOrDefaultSelector,
  hasTuitionTypeOrDefaultSelector,
  tuitionTypeOrDefaultSelector,
  durationOrDefaultSelector,
  educationTypeOrDefaultSelector,
  schoolTypeOrDefaultSelector,
  expectedTuitionOrDefaultSelector,
  nullablePropertyNameSelector,
  yearHomePurchaseOrDefaultSelector,
  nullableMarketValueSelector,
  nullableHasPlanToGetMortgageSelector,
  nullableDownPaymentSelector,
  mortgageDetailsOrDefaultSelector,
  propertyTaxOrDefaultSelector,
  nullableAnnualHomeownersInsuranceSelector,
  yesOrNoHomeownerAssociationFeeSelector,
  nullableHomeownerAssociationFeeSelector,
  largePurchaseNameOrDefaultSelector,
  largePurchaseAmountOrDefaultSelector,
  largePurchaseYearOrDefaultSelector,
  debtsOverviewSelector,
  emergencyFundTargetAmountOrDefaultSelector,
  emergencyFundTargetYearOrDefaultSelector,
  pensionsSelector,
  stepsToSkipSelector,
  healthcareGoalSelector,
  selectDistributionStrategyType,
  selectDistributionPercentage,
  selectDistributionDollarAmount,
  selectDistributionYears,
  buildItemList,
);

export const itemListDebtManagementSelector = createSelector(
  dictionarySelector,
  dataSelector,
  data2Selector,
  nullableSelectedFilingStateSelector,
  yearlySalarySelector,
  partnerYearlySalarySelector,
  selectTotalExpensesAmount,
  debtsOverviewSelector,
  buildItemDebtManagementList,
);

export const itemListOrDefaultSelector = (state, props) => {
  if (!isPopulatedSelector(state, props)) {
    return [];
  }
  if (
    props.workflowId === DEBT_MANAGEMENT_WORKFLOW_ID ||
    props.workflowId === FINANCIAL_ACCOUNTS_WORKFLOW_ID
  ) {
    return itemListDebtManagementSelector(state, props);
  }
  return itemListNonDebtManagementSelector(state, props);
};

export const sortedItemListOrDefaultSelector = (state, props) => {
  const itemListOrDefault = itemListOrDefaultSelector(state, props);
  itemListOrDefault.sort((a, b) => iteratorComparator(a.iterator, b.iterator));
  return itemListOrDefault;
};
